import { ApolloError } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import { getTempInfoCountryConfig } from '../../constants/countries';
import { LDFlags } from '../../constants/flags';
import { isTreatedAsOther } from '../../constants/lienholders';
import { ALL_DONE } from '../../constants/urls';
import {
  PayoffRequestStatus,
  TransformType,
  useExternalUserPayoffRequestLazyQuery,
  useTemporaryInfoImportMutation,
} from '../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../hooks';
import { usePayoffIntelligence } from '../../hooks/useAutoFetchPayoff/usePayoffIntelligence';
import hotjar from '../../services/analytics/hotjar';
import { trackMarketplaceTransitionViewed } from '../../services/analytics/marketplaceEvents';
import { identify } from '../../services/analytics/rudderstack';
import { logger } from '../../services/sentry';
import {
  frozenOrLockedCredit,
  getImportType,
  getTransformType,
  payoffRequestInProgressStatuses,
  sellsViaMarketplace,
} from '../../utils/tempInfo';
import { createErrorToast } from '../../utils/toast';

const useNextSteps = () => {
  const toast = useToast();
  const history = useHistory();
  const hidePayoffPageEnabled = useFlag(LDFlags.COM_HIDE_PAYOFF_PAGE);
  const marketplaceDashboard = useFlag(LDFlags.MARKETPLACE_DASHBOARD);
  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only' });

  const [fetchPayoffRequest] = useExternalUserPayoffRequestLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [importTemporaryInfo] = useTemporaryInfoImportMutation({
    context: {
      isErrorHandled: true,
    },
  });

  const { checkLienholderSupportedForPI, sendPayoffRequest } = usePayoffIntelligence();

  const wantsToSell = sellsViaMarketplace(info?.data);

  const importInfo = async () => {
    if (!info?.data) {
      return;
    }

    const countryConfig = getTempInfoCountryConfig(info.data);

    if (countryConfig.payoffCollection) {
      try {
        const { isSupported, requirementsMet, requiredFields } =
          await checkLienholderSupportedForPI();
        if (isSupported && requirementsMet && requiredFields.length) {
          await sendPayoffRequest(requiredFields);
        }
      } catch (error) {
        logger.error(
          'useNextSteps.tsx',
          'Post-flow PI trigger failed; continuing with import',
          { tempInfoId: info?.id },
          error,
        );
      }
    }

    const passedPrequal = !!info.data.prequalification_success;
    const failedPrequalification = !passedPrequal;
    const hasKbbValues = !!info.data.retail_book_value && !!info.data.book_value;
    const hasOtherLienholder = isTreatedAsOther(info.data);

    const buyerFrozenOrLockedCredit = frozenOrLockedCredit(
      info?.data?.prequalification_errors ?? [],
    );
    const cobuyerFrozenOrLockedCredit = frozenOrLockedCredit(
      info?.data?.cobuyer_prequalification_errors ?? [],
    );

    const anyFrozenOrLockedCredit = buyerFrozenOrLockedCredit || cobuyerFrozenOrLockedCredit;

    const { data: payoffRequestData } = await fetchPayoffRequest();
    const payoffRequestStatus = payoffRequestData?.externalUserPayoffRequest?.status;

    const isUnsupportedLienholder =
      hidePayoffPageEnabled &&
      (!payoffRequestStatus || payoffRequestStatus === PayoffRequestStatus.Cancelled) &&
      !hasOtherLienholder &&
      !wantsToSell;

    const transformType =
      countryConfig.importTransformType ??
      getTransformType(
        failedPrequalification,
        hasKbbValues,
        hasOtherLienholder,
        anyFrozenOrLockedCredit,
        wantsToSell,
        payoffRequestStatus,
        isUnsupportedLienholder,
      );

    const stopWtsImport =
      wantsToSell &&
      payoffRequestStatus &&
      payoffRequestInProgressStatuses.includes(payoffRequestStatus);

    const importType = stopWtsImport ? undefined : getImportType(transformType);

    try {
      const deal = await importTemporaryInfo({
        variables: {
          id: info?.id ?? '',
          transformType,
          importType,
        },
      });

      const dealInfo = deal.data?.temporaryInfoImport;
      if (dealInfo?.id && dealInfo?.customer?.id && info.data.vin) {
        identify({
          deal_id: dealInfo?.id,
          customer_id: dealInfo?.customer.id,
        });
        hotjar.identify(info.data.vin, {
          deal_id: dealInfo.id,
        });
        if (wantsToSell && marketplaceDashboard && info.id) {
          trackMarketplaceTransitionViewed(info.id, dealInfo.id);
        }
      }

      if (transformType === TransformType.AcquisitionNotApproved && !marketplaceDashboard) {
        history.replace({ pathname: ALL_DONE });
      }
    } catch (e: unknown) {
      if (!(e instanceof ApolloError && e?.graphQLErrors?.[0]?.extensions?.code)) {
        toast(createErrorToast({ errorMessage: (e as ApolloError).message }));
      }
    }
  };

  return {
    importInfo,
    loading: infoLoading,
    wantsToSell,
    marketplaceDashboard,
  };
};

export default useNextSteps;
